import { BookOpen, Lightbulb, Layers, Clock } from "lucide-react";
import type { RecentItem } from "../api/graph";
import type { CatalogItem, ListKind } from "../types/navigator";
import { LikeButton } from "./LikeButton";

const KIND_ICON = {
  library: BookOpen,
  initiative: Lightbulb,
  platform: Layers,
} as const;

const KIND_LABEL: Record<ListKind, string> = {
  library: "Library",
  initiative: "Initiative",
  platform: "Platform",
};

function formatDate(iso?: string) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

type Props = {
  items: RecentItem[];
  loading?: boolean;
  error?: string | null;
  onOpen: (item: CatalogItem) => void;
};

export function RecentStrip({ items, loading, error, onOpen }: Props) {
  return (
    <section className="dash-section">
      <div className="dash-section-head">
        <h2 className="dash-section-title">
          <Clock size={16} /> Recently updated
        </h2>
        <p className="dash-section-sub">
          The latest assets across Library, Initiatives, and Platforms.
        </p>
      </div>
      {error ? (
        <div className="dash-strip-empty">{error}</div>
      ) : loading ? (
        <div className="recent-strip">
          {[0, 1, 2, 3].map((i) => (
            <div className="recent-card recent-skeleton" key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="dash-strip-empty">No recent activity.</div>
      ) : (
        <div className="recent-strip">
          {items.map(({ item, kind }) => {
            const Icon = KIND_ICON[kind];
            return (
              <button
                key={`${kind}-${item.id}`}
                className={`recent-card recent-${kind}`}
                onClick={() => onOpen(item)}
              >
                <div className="recent-card-head">
                  <span className="recent-card-kind">
                    <Icon size={14} strokeWidth={1.75} />
                    {KIND_LABEL[kind]}
                  </span>
                  <span className="recent-card-date">
                    {formatDate(item.modified || item.created)}
                  </span>
                </div>
                <div className="recent-card-title">{item.title}</div>
                {item.description ? (
                  <div className="recent-card-desc">{item.description}</div>
                ) : null}
                {item.kind === "library" ? (
                  <div className="recent-card-foot">
                    <LikeButton item={item} />
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
